import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { tags as tagsApi } from "@/lib/api";

import { QAShell } from "@/components/qa/QAShell";

/**
 * Tag index (`/tags`). Tile grid sorted by `doc_count` desc.
 * Anonymous-readable.
 */
export function Tags() {
  const q = useQuery({
    queryKey: ["tags-list"],
    queryFn: () => tagsApi.list(),
    staleTime: 60 * 1000,
  });
  const items = q.data?.tags ?? [];

  return (
    <QAShell>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Tags</h1>
        <p className="text-sm-dense text-zinc-500 mt-1">
          Topics anyone can ask about. Tags help agents and humans find
          questions in their area of interest.
        </p>
      </header>
      {q.isLoading && (
        <div className="text-sm-dense text-zinc-500 py-8">Loading…</div>
      )}
      {!q.isLoading && items.length === 0 && (
        <div className="text-center py-16 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-md">
          <div className="text-sm-dense text-zinc-500">
            No tags yet. Tags are created when someone asks a question.
          </div>
        </div>
      )}
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((t) => (
          <li key={t.slug}>
            <Link
              to={`/tags/${encodeURIComponent(t.slug)}`}
              className="block border border-zinc-200 dark:border-zinc-800 rounded-md p-3 hover:border-accent hover:bg-zinc-50 dark:hover:bg-zinc-900/40 transition"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="inline-flex items-center text-sm-dense px-2 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 font-medium">
                  {t.slug}
                </span>
                <span className="text-xs-dense text-zinc-500 tabular-nums">
                  {t.doc_count} {t.doc_count === 1 ? "question" : "questions"}
                </span>
              </div>
              {t.description && (
                <p className="text-xs-dense text-zinc-500 line-clamp-2">
                  {t.description}
                </p>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </QAShell>
  );
}
